'use client';

import type { Placement } from '@floating-ui/react';
import React from 'react';
import { Popup } from '@/app/components/Popup';
import { Tooltip } from '@/app/components/Tooltip';
import { TooltipWrapper } from '@/app/components/Tooltip/components/TooltipWrapper';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { cn } from '@/utils/сn';
import { contextMenuContainerStyles, contextMenuItemStyles } from './styles';

export interface MenuItemConfig {
  name: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  action: () => void;
  isDanger?: boolean;
  isDisabled?: boolean;
  infoTooltip?: string;
  iconClassName?: string;
  hideDivider?: boolean;
}

interface ContextMenuProps {
  items: MenuItemConfig[];
  trigger: React.ReactNode | ((isOpen: boolean, close: VoidFunction, toggle: VoidFunction) => React.ReactNode);
  placement?: Placement;
  triggerWrapperClassName?: string;
  iconClassName?: string;
  containerClassName?: string;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  trigger,
  placement,
  triggerWrapperClassName,
  iconClassName,
  containerClassName,
}) => {
  const isMobileViewport = useIsMobileViewport();

  const handleItemClick = (item: MenuItemConfig, closePopup: () => void) => {
    if (item.isDisabled) {
      return;
    }
    item.action();
    closePopup();
  };

  return (
    <Popup
      trigger={trigger}
      placement={placement}
      triggerWrapperClassName={triggerWrapperClassName}
      onToggleTrigger={true}
    >
      {(close) => (
        <div className={cn(contextMenuContainerStyles(), containerClassName)}>
          {items.map((item) => (
            <Tooltip
              key={item.name}
              isVisible={!!item.infoTooltip}
              content={
                <TooltipWrapper arrow={isMobileViewport ? 'bottom' : 'right'}>{item.infoTooltip}</TooltipWrapper>
              }
              placement={isMobileViewport ? 'top' : 'left'}
            >
              <button
                className={contextMenuItemStyles({
                  isDanger: item.isDanger && !item.isDisabled,
                  isDisabled: item.isDisabled,
                  hideDivider: item.hideDivider,
                })}
                onClick={() => handleItemClick(item, close)}
                disabled={item.isDisabled}
                type="button"
              >
                {item.icon && (
                  <item.icon
                    className={cn(
                      item.iconClassName || iconClassName || 'w-4 h-4 max-md:w-6 max-md:h-6',
                      item.isDanger && !item.isDisabled && 'text-red-500',
                      item.isDisabled && 'text-neutral-400'
                    )}
                    aria-hidden="true"
                  />
                )}
                <span>{item.name}</span>
              </button>
            </Tooltip>
          ))}
        </div>
      )}
    </Popup>
  );
};

export default ContextMenu;
